import {useEffect, useState} from "react";
import {Dialog, DialogActions, DialogContent, Stack} from "@mui/material";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import * as AboutContext from "./AboutContext";
import {damsFetch} from "../app/damsFetch";
import {ReleaseNote} from "./RelaseNote";
import {clientLog} from "../clientLog";
import {useSnackbar} from "notistack";
import {useTranslation} from "react-i18next";

/**
 * AboutDialog component displays a modal dialog with release notes.
 * It fetches release notes from the server and displays them in a list.
 * The dialog can be closed using the "Lukk" button.
 */
export const AboutDialog = () => {

    const {t} = useTranslation("dams.document", {useSuspense: false});

    /**
     * Snackbar function to display notifications.
     */
    const {enqueueSnackbar} = useSnackbar();

    /**
     * Dispatch function to update the AboutContext state.
     */
    const aboutDispatch = AboutContext.useAboutDispatch();

    /**
     * State indicating whether the AboutDialog is open.
     */
    const {open} = AboutContext.useAboutState();

    /**
     * State indicating whether the release notes are loading.
     */
    const [loading, setLoading] = useState(true);

    /**
     * State to store the fetched release notes.
     */
    const [releaseNotes, setReleaseNotes] = useState([]);

    /**
     * Handles the click event to close the AboutDialog.
     */
    const handleCloseClick = () => {
        aboutDispatch({
            type: AboutContext.CLOSE_ABOUT_DIALOG
        });
    };

    /**
     * Effect to load release notes when the dialog is opened.
     */
    useEffect(() => {
        /**
         * Fetches release notes from the server.
         */
        const load = async () => {
            try {
                const data = await damsFetch('/release-notes/');
                const {items} = data;
                setReleaseNotes(items);
            } catch (error) {
                clientLog('error', 'failed to load release notes: ' + error, 'aboutDialog');
                enqueueSnackbar({
                    message: 'Kunne ikke laste release notes',
                    options: {
                        variant: 'error',
                    }
                });
            }
        };

        if (!open) {
            return;
        }
        (async () => await load())();
    }, [open, enqueueSnackbar]);

    /**
     * Effect to stop loading when release notes are fetched.
     */
    useEffect(() => {
        if (releaseNotes.length > 0) {
            setLoading(false);
        }
    }, [releaseNotes]);

    return (
        <Dialog open={open && !loading} onClose={handleCloseClick}>
            <DialogTitle>{t('aboutDams', 'Om DAMS')}</DialogTitle>
            <DialogContent>
                {
                    releaseNotes.map((releaseNote) => (<ReleaseNote key={releaseNote.id} releaseNote={releaseNote}/>))
                }
            </DialogContent>
            <DialogActions>
                <Stack direction="row" sx={{
                    justifyContent: "flex-end"
                }}>
                    <Button onClick={handleCloseClick}>{t('btnClose', 'Lukk')}</Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );
};